import type { CurriculumPack } from '@/content/schema';

export const meta = {
  schemaVersion: 1,
  id: 'tn-primary',
  educationSystem: {
    id: 'tn',
    name: {
      ar: 'المنظومة التربوية التونسية',
      fr: 'Système éducatif tunisien',
      en: 'Tunisian education system',
    },
  },
  curriculumVersion: {
    id: 'tn-primary-2026-2027-sample',
    academicYear: '2026-2027',
    label: {
      ar: 'محتوى تجريبي 2026–2027 (في انتظار المصادقة)',
      fr: 'Contenu d’exemple 2026–2027 (en attente de validation)',
      en: 'Sample content 2026–2027 (awaiting validation)',
    },
    status: 'published',
    verification: 'unverified-sample',
    notes:
      'Sample content written for the MVP. Level placement follows the public structure of the Tunisian programmes ' +
      '(three degrees of two years) but no objective has been checked against the official programme documents yet. ' +
      'See docs/CURRICULUM-VALIDATION.md.',
  },
  sources: [
    {
      id: 'tn-moe-programmes',
      title: 'البرامج الرسمية',
      publisher: 'وزارة التربية — الجمهورية التونسية',
      url: 'https://education.gov.tn/?p=500',
      accessedAt: '2026-10-19',
      notes:
        'Index of official programmes by degree (degree 1: years 1–2, degree 2: years 3–4, degree 3: years 5–6). ' +
        'Most linked PDFs on edunet.tn returned HTTP 403 when accessed, so their content is not reflected here.',
    },
    {
      id: 'tn-moe-english-degree3',
      title: 'English Club Programs for the Fifth and Sixth Year Basic Education',
      publisher: 'وزارة التربية — الجمهورية التونسية (edunet.tn)',
      url: 'http://www.edunet.tn/ressources/pedagogie/programmes/langues/anglais/anglais_degre3.pdf',
      accessedAt: '2026-10-19',
      notes:
        'Lists communicative functions (greetings, numbers, colours, family, pets, days…). Older document; current textbooks may differ.',
    },
    {
      id: 'tn-2019-languages-announcement',
      title:
        'Announcement: French from year 2 and English from year 4 of primary school (2019/2020)',
      publisher: 'Press report of a Ministry of Education announcement (2 July 2019)',
      url: 'https://orientini.com/AR/Annonces_Orientation_Formation_Emploi/6492/وزارة-التربية-:-تدريس-مادة-الفرنسية-في-السنة-الثانية-ابتدائي-ومادة-الإنجليزية-في-السنة-الرابعة-ابتدائي',
      accessedAt: '2026-10-19',
      notes: 'Secondary source. Whether this is the current organisation must be confirmed.',
    },
  ],
  levels: [
    {
      id: 'prep',
      order: 0,
      stage: 'preschool',
      typicalAge: { min: 5, max: 6 },
      name: { ar: 'التحضيري', fr: 'Préparatoire', en: 'Preschool' },
    },
    {
      id: 'y1',
      order: 1,
      stage: 'degree1',
      typicalAge: { min: 6, max: 7 },
      name: { ar: 'السنة الأولى', fr: '1re année', en: 'Year 1' },
    },
    {
      id: 'y2',
      order: 2,
      stage: 'degree1',
      typicalAge: { min: 7, max: 8 },
      name: { ar: 'السنة الثانية', fr: '2e année', en: 'Year 2' },
    },
    {
      id: 'y3',
      order: 3,
      stage: 'degree2',
      typicalAge: { min: 8, max: 9 },
      name: { ar: 'السنة الثالثة', fr: '3e année', en: 'Year 3' },
    },
    {
      id: 'y4',
      order: 4,
      stage: 'degree2',
      typicalAge: { min: 9, max: 10 },
      name: { ar: 'السنة الرابعة', fr: '4e année', en: 'Year 4' },
    },
    {
      id: 'y5',
      order: 5,
      stage: 'degree3',
      typicalAge: { min: 10, max: 11 },
      name: { ar: 'السنة الخامسة', fr: '5e année', en: 'Year 5' },
    },
    {
      id: 'y6',
      order: 6,
      stage: 'degree3',
      typicalAge: { min: 11, max: 12 },
      name: { ar: 'السنة السادسة', fr: '6e année', en: 'Year 6' },
    },
  ],
  schoolTypes: [
    { id: 'public', name: { ar: 'مدرسة عموميّة', fr: 'École publique', en: 'Public school' } },
    { id: 'private', name: { ar: 'مدرسة خاصّة', fr: 'École privée', en: 'Private school' } },
  ],
  variants: [
    {
      id: 'tn-national',
      name: { ar: 'البرنامج الوطني', fr: 'Programme national', en: 'National programme' },
      description: {
        ar: 'البرنامج الرسمي المشترك. لم نُدرج أيّ اختلاف خاصّ بالمدارس الخاصّة لأنّه غير مُتحقَّق منه بعد.',
        fr: 'Programme officiel commun. Aucune différence propre aux écoles privées n’est modélisée tant qu’elle n’est pas vérifiée.',
        en: 'The common official programme. No private-school differences are modelled until they are verified.',
      },
      schoolTypes: ['public', 'private'],
      status: 'published',
      verification: 'needs-review',
    },
  ],
} satisfies Omit<CurriculumPack, 'subjects' | 'domains' | 'objectives' | 'lessons' | 'items'>;
